// API Service
// Centralized service for all backend API communications
// Handles authentication, error handling, and response formatting

import { API_CONFIG, getApiUrl, getHealthUrl } from '@/config/apiConfig';
import { buildRequestSigningHeaders } from '@/utils/requestSigning';
import { getStoredLanguage } from '@/i18n/language';

/** Same-tab login/logout listeners (e.g. notification bell visibility). */
export const KIOSK_AUTH_TOKEN_CHANGED_EVENT = 'kiosk-auth-token-changed';

export interface ApiResponse<T = any> {
    success: boolean;
    message: string;
    data?: T;
    error?: string;
    statusCode?: number;
    timestamp?: string;
    errors?: Array<{
        field: string;
        message: string;
    }>;
}

export interface ApiError {
    message: string;
    statusCode: number;
    code?: string;
    error?: string;
    errors?: Array<{
        field: string;
        message: string;
    }>;
    data?: any;
    details?: Record<string, unknown>;
}

// Cache configuration – tuned for lower network chatter on kiosk clients.
const CACHE_TTL = 30 * 1000; // 30 seconds for balance/profile
const PROFILE_CACHE_TTL = 60 * 1000; // 60 seconds for profile data
const USER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes for current user
const TX_HISTORY_CACHE_TTL = 30 * 1000; // 30 seconds for transaction history
const CACHE_VERSION_HEADER = 'x-cache-version';

interface CacheEntry<T> {
    data: ApiResponse<T>;
    timestamp: number;
}

class ApiService {
    private readonly BASE_URL = API_CONFIG.BASE_URL;
    private readonly API_PREFIX = '/api';

    // Token management
    private accessToken: string | null = null;
    private refreshToken: string | null = null;
    /** Lock: only one refresh in flight; concurrent 401s share the same refresh result */
    private refreshPromise: Promise<boolean> | null = null;

    // Cache storage
    private balanceCache: CacheEntry<{ balance: number; currency: string; lastUpdated: string }> | null = null;
    private profileCache: CacheEntry<{ user: any }> | null = null;
    private currentUserCache: CacheEntry<any> | null = null;
    private txHistoryCache: Map<string, CacheEntry<any>> = new Map();
    /** In-flight GET dedupe map to prevent concurrent duplicate network calls. */
    private pendingGetRequests: Map<string, Promise<ApiResponse<any>>> = new Map();
    /** Optional server-driven version per endpoint for cache invalidation. */
    private endpointCacheVersions: Map<string, string> = new Map();

    constructor() {
        // Load tokens from localStorage on initialization
        this.loadTokensFromStorage();
    }

    /**
     * Loads authentication tokens from localStorage
     */
    private loadTokensFromStorage(): void {
        if (typeof window !== 'undefined') {
            this.accessToken = localStorage.getItem('accessToken');
            this.refreshToken = localStorage.getItem('refreshToken');
        }
    }

    /**
     * Saves authentication tokens to localStorage
     */
    private saveTokensToStorage(accessToken: string, refreshToken: string): void {
        if (typeof window !== 'undefined') {
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            this.accessToken = accessToken;
            this.refreshToken = refreshToken;
            window.dispatchEvent(new CustomEvent(KIOSK_AUTH_TOKEN_CHANGED_EVENT));
        }
    }

    /**
     * Clears authentication tokens from localStorage
     */
    private clearTokensFromStorage(): void {
        if (typeof window !== 'undefined') {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            this.accessToken = null;
            this.refreshToken = null;
            window.dispatchEvent(new CustomEvent(KIOSK_AUTH_TOKEN_CHANGED_EVENT));
        }
    }

    /**
     * Gets the full API URL for an endpoint
     */
    private getApiUrl(endpoint: string): string {
        return getApiUrl(endpoint);
    }

    /**
     * Gets headers for API requests
     */
    private getHeaders(includeAuth: boolean = true): HeadersInit {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };

        // Language: drive backend localization (errors/templates) from FE-selected locale.
        // Header-only model: backend treats this as source of truth.
        headers['Accept-Language'] = getStoredLanguage();
        headers['X-Language'] = getStoredLanguage();

        if (includeAuth) {
            // Always reload token from localStorage to ensure we have the latest token
            // This handles cases where token was set in another tab or after login
            if (typeof window !== 'undefined') {
                const tokenFromStorage = localStorage.getItem('accessToken');
                if (tokenFromStorage) {
                    this.accessToken = tokenFromStorage;
                }
            }

            if (this.accessToken) {
                headers['Authorization'] = `Bearer ${this.accessToken}`;
            }
        }

        return headers;
    }

    private async getHeadersWithSigning(
        method: 'GET' | 'POST' | 'PUT' | 'DELETE',
        endpoint: string,
        includeAuth: boolean,
        body?: unknown
    ): Promise<HeadersInit> {
        const headers = this.getHeaders(includeAuth) as Record<string, string>;
        const signingHeaders = await buildRequestSigningHeaders(method, endpoint, body);
        return { ...headers, ...signingHeaders };
    }

    /**
     * Handles API response and converts to standardized format
     */
    private async handleResponse<T>(response: Response, includeAuth: boolean = true): Promise<ApiResponse<T>> {
        let data: any;
        let responseText: string = '';

        try {
            responseText = await response.text();
            // Try to parse JSON, if empty or invalid, use empty object
            data = responseText ? JSON.parse(responseText) : {};
        } catch (parseError: any) {
            console.error('Failed to parse API response as JSON:', {
                status: response.status,
                statusText: response.statusText,
                url: response.url,
                parseError: parseError?.message || String(parseError),
                responseText: responseText.substring(0, 500) // First 500 chars for debugging
            });
            // If JSON parsing fails, create a structured error response
            throw {
                message: `Invalid response from server (status: ${response.status}). Expected JSON but received: ${responseText.substring(0, 100)}`,
                statusCode: response.status,
                error: 'INVALID_RESPONSE',
                responseText: responseText
            };
        }

        if (!response.ok) {
            const error: ApiError = {
                message: data.message || 'An error occurred',
                statusCode: response.status,
                code: data.code,
                error: data.error,
                errors: data.errors,
                data: data.data || data,
                details: data.details,
            };

            // Handle specific error cases
            if (response.status === 401) {
                // Debug: log when we consider refresh (Step 1 — remove after confirming refresh works)
                if (typeof window !== 'undefined' && import.meta.env?.DEV) {
                    console.debug('[apiService] 401 received', { includeAuth, hasRefreshToken: !!this.refreshToken });
                }
                // Business 401s (bad OTP / wrong password) must not trigger session refresh —
                // otherwise UI surfaces the internal "RETRY_REQUEST" sentinel.
                const msg = String(error.message || '').toLowerCase();
                const isBusinessAuthFailure =
                    msg.includes('verification code') ||
                    msg.includes('invalid code') ||
                    msg.includes('email code') ||
                    msg.includes('invalid username or password') ||
                    msg.includes('invalid email or password') ||
                    msg.includes('invalid credentials') ||
                    msg.includes('invalid password') ||
                    msg.includes('2fa is enabled');

                // Only attempt token refresh for authenticated requests (tryRefresh reloads refreshToken from storage)
                if (includeAuth && !isBusinessAuthFailure) {
                    const refreshSuccess = await this.tryRefresh();
                    if (refreshSuccess) {
                        // Retry the original request
                        throw new Error('RETRY_REQUEST');
                    }
                    // Authenticated call with dead session: clear tokens and nudge re-login
                    this.clearTokensFromStorage();
                    this.clearUserCache();

                    if (typeof window !== 'undefined') {
                        const currentPath = window.location.pathname;
                        if (currentPath !== '/' && currentPath !== '/login') {
                            window.location.href = '/';
                        }
                    }
                }
                // includeAuth === false: 401 usually means wrong password / invalid OTP on a public endpoint.
                // Do not clear tokens or redirect — only surface the error to the caller.
            }

            throw error;
        }

        // For successful HTTP status codes (200-299), check if API explicitly returns success: false
        // If API doesn't specify success field, default to true for 2xx status codes
        // If API explicitly returns success: false with 2xx status, preserve it (for validation errors, etc.)
        const apiSuccess = data.success !== undefined ? data.success : true;

        // Extract data field - handle both nested and flat structures
        let extractedData: any;
        if (data.data !== undefined) {
            // Standard format: {success: true, data: {...}}
            extractedData = data.data;
        } else if (apiSuccess) {
            // Flat format when successful: {success: true, token: ..., user: ...}
            // Exclude metadata fields from data
            const { success: _success, message: _message, timestamp: _timestamp, errors: _errors, ...rest } = data;
            extractedData = Object.keys(rest).length > 0 ? rest : data;
        } else {
            // Error case: return undefined for data
            extractedData = undefined;
        }

        // Ensure message is always a string, never empty or undefined
        const finalMessage = data.message && data.message.trim()
            ? data.message.trim()
            : (apiSuccess ? 'Success' : 'An error occurred');

        return {
            success: apiSuccess,
            message: finalMessage,
            data: extractedData,
            statusCode: response.status,
            timestamp: data.timestamp,
            errors: data.errors
        };
    }

    /**
     * Checks if cache is valid (exists and not expired)
     * @param cache - Cache entry to check
     * @param ttl - Time to live in milliseconds (default: CACHE_TTL)
     */
    private isCacheValid<T>(cache: CacheEntry<T> | null, ttl: number = CACHE_TTL): boolean {
        if (!cache) {
            return false;
        }
        const now = Date.now();
        const age = now - cache.timestamp;
        return age < ttl;
    }

    /**
     * Gets cached data if available and valid
     * @param cache - Cache entry to get
     * @param ttl - Time to live in milliseconds (default: CACHE_TTL)
     */
    private getCachedData<T>(cache: CacheEntry<T> | null, ttl: number = CACHE_TTL): ApiResponse<T> | null {
        if (this.isCacheValid(cache, ttl) && cache) {
            return cache.data;
        }
        return null;
    }

    /**
     * Sets cache with new data
     */
    private setCache<T>(cacheKey: 'balance' | 'profile' | 'currentUser', data: ApiResponse<T>): void {
        const cacheEntry: CacheEntry<T> = {
            data,
            timestamp: Date.now()
        };

        switch (cacheKey) {
            case 'balance':
                this.balanceCache = cacheEntry as CacheEntry<{ balance: number; currency: string; lastUpdated: string }>;
                break;
            case 'profile':
                this.profileCache = cacheEntry as CacheEntry<{ user: any }>;
                break;
            case 'currentUser':
                this.currentUserCache = cacheEntry;
                break;
        }
    }

    private clearCacheForEndpoint(endpoint: string): void {
        if (endpoint === '/payment/balance' || endpoint === '/wallet/balance') {
            this.balanceCache = null;
            return;
        }
        if (endpoint === '/users/profile') {
            this.profileCache = null;
            return;
        }
        if (endpoint === '/auth/me') {
            this.currentUserCache = null;
            return;
        }
        if (endpoint.startsWith('/payment/history')) {
            this.txHistoryCache.delete(endpoint);
        }
    }

    /**
     * Server may return `x-cache-version` to signal data changes.
     * When version changes for an endpoint, clear corresponding local cache.
     */
    private syncEndpointCacheVersion(endpoint: string, response: Response): void {
        const nextVersion = response.headers.get(CACHE_VERSION_HEADER);
        if (!nextVersion) return;

        const prevVersion = this.endpointCacheVersions.get(endpoint);
        this.endpointCacheVersions.set(endpoint, nextVersion);

        if (prevVersion && prevVersion !== nextVersion) {
            this.clearCacheForEndpoint(endpoint);
            if (import.meta.env.DEV) {
                console.log('[ApiService] Cache invalidated by server version change:', {
                    endpoint,
                    previousVersion: prevVersion,
                    nextVersion,
                });
            }
        }
    }

    /**
     * Clears all user-related caches
     * Should be called after deposit, withdrawal, login, logout
     */
    clearUserCache(): void {
        this.balanceCache = null;
        this.profileCache = null;
        this.currentUserCache = null;
        this.txHistoryCache.clear();
        if (import.meta.env.DEV) console.log('[ApiService] User cache cleared');
    }


    /**
     * Pre-fetches all important user data in background after login
     * This caches balance, profile, current user, and game providers for faster subsequent loads
     * Runs in background (non-blocking) and silently handles errors
     */
    prefetchUserData(): void {
        // Run in background without blocking - don't await, let it run asynchronously
        // Use setTimeout to ensure it doesn't block the main thread
        setTimeout(async () => {
            try {
                if (import.meta.env.DEV) console.log('[ApiService] Starting background pre-fetch...');

                // Import game providers service dynamically to avoid circular dependency
                const { getGameProviders } = await import('@/features/games/services/gameProviderService');

                // Pre-fetch all important APIs in parallel
                const prefetchPromises = [
                    this.get<{ balance: number; currency: string; lastUpdated: string }>('/payment/balance', true, false)
                        .catch(() => { /* silent */ }),
                    this.get<{ user: any }>('/users/profile', true, false)
                        .catch(() => { /* silent */ }),
                    this.get<any>('/auth/me', true, false)
                        .catch(() => { /* silent */ }),
                    getGameProviders(false)
                        .catch(() => { /* silent */ })
                ];

                // Wait for all to complete (or fail silently)
                await Promise.allSettled(prefetchPromises);

                if (import.meta.env.DEV) console.log('[ApiService] Pre-fetch completed');
            } catch {
                // Silent – background pre-fetch
            }
        }, 100); // Small delay to ensure login flow completes first
    }

    /**
     * Makes a GET request to the API
     * Supports caching for common endpoints. Deduplication is handled via cache.
     */
    async get<T>(endpoint: string, includeAuth: boolean = true, forceRefresh: boolean = false): Promise<ApiResponse<T>> {
        const requestKey = `${includeAuth ? 'auth' : 'public'}:${endpoint}`;
        try {
            // ── 1. Check in-memory cache ─────────────────────────────────
            if (!forceRefresh && includeAuth) {
                if (endpoint === '/payment/balance' || endpoint === '/wallet/balance') {
                    const cached = this.getCachedData<any>(this.balanceCache);
                    if (cached) return cached as ApiResponse<T>;
                }
                if (endpoint === '/users/profile') {
                    const cached = this.getCachedData<any>(this.profileCache, PROFILE_CACHE_TTL);
                    if (cached) return cached as ApiResponse<T>;
                }
                if (endpoint === '/auth/me') {
                    const cached = this.getCachedData<any>(this.currentUserCache, USER_CACHE_TTL);
                    if (cached) return cached as ApiResponse<T>;
                }
                if (endpoint.startsWith('/payment/history')) {
                    const txCached = this.txHistoryCache.get(endpoint);
                    if (txCached && (Date.now() - txCached.timestamp) < TX_HISTORY_CACHE_TTL) {
                        return txCached.data as ApiResponse<T>;
                    }
                }
            }

            // ── 1.5. Reuse in-flight request (dedupe burst calls) ───────
            if (!forceRefresh) {
                const pending = this.pendingGetRequests.get(requestKey);
                if (pending) {
                    return pending as Promise<ApiResponse<T>>;
                }
            }

            // ── 2. Fetch from API ────────────────────────────────────────
            const requestPromise = (async () => {
                const response = await fetch(this.getApiUrl(endpoint), {
                    method: 'GET',
                    headers: this.getHeaders(includeAuth),
                });
                this.syncEndpointCacheVersion(endpoint, response);

                const result = await this.handleResponse<T>(response, includeAuth);

                // ── 3. Update caches on success ──────────────────────────────
                if (result.success && includeAuth) {
                    if (endpoint === '/payment/balance' || endpoint === '/wallet/balance') {
                        this.setCache('balance', result as any);
                    } else if (endpoint === '/users/profile') {
                        this.setCache('profile', result as any);
                    } else if (endpoint === '/auth/me') {
                        this.setCache('currentUser', result);
                    } else if (endpoint.startsWith('/payment/history')) {
                        this.txHistoryCache.set(endpoint, { data: result as any, timestamp: Date.now() });
                    }
                }

                return result;
            })();

            if (!forceRefresh) {
                this.pendingGetRequests.set(requestKey, requestPromise as Promise<ApiResponse<any>>);
            }

            try {
                return await requestPromise;
            } finally {
                if (!forceRefresh) {
                    this.pendingGetRequests.delete(requestKey);
                }
            }
        } catch (error) {
            if (error instanceof Error && error.message === 'RETRY_REQUEST') {
                // Retry the request with new token
                const response = await fetch(this.getApiUrl(endpoint), {
                    method: 'GET',
                    headers: this.getHeaders(includeAuth),
                });
                return await this.handleResponse<T>(response, includeAuth);
            }

            // Fallback to stale cache on network errors
            if (includeAuth) {
                if (endpoint === '/payment/balance' || endpoint === '/wallet/balance') {
                    if (this.balanceCache) return this.balanceCache.data as ApiResponse<T>;
                } else if (endpoint === '/users/profile') {
                    if (this.profileCache) return this.profileCache.data as ApiResponse<T>;
                } else if (endpoint === '/auth/me') {
                    if (this.currentUserCache) return this.currentUserCache.data as ApiResponse<T>;
                }
            }

            throw this.handleNetworkError(error);
        }
    }

    /**
     * Makes a POST request to the API
     */
    async post<T>(endpoint: string, data: any, includeAuth: boolean = true): Promise<ApiResponse<T>> {
        try {
            const headers = await this.getHeadersWithSigning('POST', endpoint, includeAuth, data);
            const response = await fetch(this.getApiUrl(endpoint), {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });

            return await this.handleResponse<T>(response, includeAuth);
        } catch (error) {
            if (error instanceof Error && error.message === 'RETRY_REQUEST') {
                // Retry the request with new token
                const headers = await this.getHeadersWithSigning('POST', endpoint, includeAuth, data);
                const response = await fetch(this.getApiUrl(endpoint), {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(data),
                });
                return await this.handleResponse<T>(response, includeAuth);
            }
            throw this.handleNetworkError(error);
        }
    }

    /**
     * Makes a PUT request to the API
     */
    async put<T>(endpoint: string, data: any, includeAuth: boolean = true): Promise<ApiResponse<T>> {
        try {
            const headers = await this.getHeadersWithSigning('PUT', endpoint, includeAuth, data);
            const response = await fetch(this.getApiUrl(endpoint), {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });

            return await this.handleResponse<T>(response, includeAuth);
        } catch (error) {
            if (error instanceof Error && error.message === 'RETRY_REQUEST') {
                // Retry the request with new token
                const headers = await this.getHeadersWithSigning('PUT', endpoint, includeAuth, data);
                const response = await fetch(this.getApiUrl(endpoint), {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(data),
                });
                return await this.handleResponse<T>(response, includeAuth);
            }
            throw this.handleNetworkError(error);
        }
    }

    /**
     * Makes a DELETE request to the API
     */
    async delete<T>(endpoint: string, includeAuth: boolean = true): Promise<ApiResponse<T>> {
        try {
            const response = await fetch(this.getApiUrl(endpoint), {
                method: 'DELETE',
                headers: this.getHeaders(includeAuth),
            });

            return await this.handleResponse<T>(response, includeAuth);
        } catch (error) {
            if (error instanceof Error && error.message === 'RETRY_REQUEST') {
                // Retry the request with new token
                const response = await fetch(this.getApiUrl(endpoint), {
                    method: 'DELETE',
                    headers: this.getHeaders(includeAuth),
                });
                return await this.handleResponse<T>(response, includeAuth);
            }
            throw this.handleNetworkError(error);
        }
    }

    /**
     * Runs at most one refresh at a time; concurrent 401s await the same refresh result.
     */
    private async tryRefresh(): Promise<boolean> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }
        this.refreshPromise = this.refreshAccessToken();
        try {
            return await this.refreshPromise;
        } finally {
            this.refreshPromise = null;
        }
    }

    /**
     * Refreshes the access token using the refresh token
     */
    private async refreshAccessToken(): Promise<boolean> {
        // Step 3: always use latest refresh token from storage (e.g. from another tab)
        if (typeof window !== 'undefined') {
            const fromStorage = localStorage.getItem('refreshToken');
            this.refreshToken = fromStorage;
        }
        if (!this.refreshToken) {
            if (typeof window !== 'undefined' && import.meta.env?.DEV) {
                console.debug('[apiService] refreshAccessToken: no refreshToken in memory/storage, skipping');
            }
            return false;
        }

        try {
            if (typeof window !== 'undefined' && import.meta.env?.DEV) {
                console.debug('[apiService] refreshAccessToken: calling POST /auth/refresh-token');
            }
            const response = await fetch(this.getApiUrl('/auth/refresh-token'), {
                method: 'POST',
                headers: this.getHeaders(false),
                body: JSON.stringify({ refreshToken: this.refreshToken }),
            });

            if (typeof window !== 'undefined' && import.meta.env?.DEV) {
                console.debug('[apiService] refreshAccessToken: response.ok=', response.ok, 'status=', response.status);
            }
            if (response.ok) {
                const data = await response.json();
                if (typeof window !== 'undefined' && import.meta.env?.DEV) {
                    console.debug('[apiService] refreshAccessToken: parsed data', {
                        success: data?.success,
                        hasData: !!data?.data,
                        hasToken: !!data?.data?.token,
                        hasRefreshToken: !!data?.data?.refreshToken,
                    });
                }
                if (data.success && data.data) {
                    this.saveTokensToStorage(data.data.token, data.data.refreshToken);
                    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
                        console.debug('[apiService] refreshAccessToken: tokens saved, returning true');
                    }
                    return true;
                }
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
        }

        return false;
    }

    /**
     * Handles network errors and converts to standardized format
     */
    private handleNetworkError(error: any): ApiError {
        // Enhanced error logging
        const errorInfo: any = {
            type: error?.constructor?.name || typeof error,
            message: error?.message,
            statusCode: error?.statusCode,
            error: error?.error,
            name: error?.name,
        };

        // Try to stringify the full error for debugging
        try {
            errorInfo.fullError = JSON.stringify(error, Object.getOwnPropertyNames(error));
        } catch (e) {
            errorInfo.fullError = String(error);
        }

        // Add stack trace if available
        if (error?.stack) {
            errorInfo.stack = error.stack;
        }

        // Log all error properties
        console.error('API Network Error:', errorInfo);

        // Check if it's already a formatted ApiError
        if (error && typeof error === 'object' && 'statusCode' in error && 'message' in error) {
            return error as ApiError;
        }

        // Handle network/fetch errors
        if (error instanceof TypeError && error.message?.includes('fetch')) {
            return {
                message: 'Unable to connect to the server. Please check your internet connection.',
                statusCode: 0,
                error: 'NETWORK_ERROR'
            };
        }

        // Handle AbortError (request cancelled)
        if (error instanceof Error && error.name === 'AbortError') {
            return {
                message: 'Request was cancelled. Please try again.',
                statusCode: 0,
                error: 'REQUEST_CANCELLED'
            };
        }

        // Extract message from various error formats
        let errorMessage = 'An unexpected error occurred. Please try again.';
        if (error?.message) {
            errorMessage = error.message;
        } else if (typeof error === 'string') {
            errorMessage = error;
        } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
        }

        return {
            message: errorMessage,
            statusCode: error?.statusCode || error?.response?.status || 500,
            error: error?.error || 'UNKNOWN_ERROR'
        };
    }

    /**
     * Sets authentication tokens
     */
    setTokens(accessToken: string, refreshToken: string): void {
        this.saveTokensToStorage(accessToken, refreshToken);
    }

    /**
     * Clears authentication tokens
     */
    clearTokens(): void {
        this.clearTokensFromStorage();
    }

    /**
     * Checks if user is authenticated
     */
    isAuthenticated(): boolean {
        return !!this.accessToken;
    }

    /**
     * Gets the current access token
     */
    getAccessToken(): string | null {
        // Always reload from localStorage to ensure we have the latest token
        if (typeof window !== 'undefined') {
            const tokenFromStorage = localStorage.getItem('accessToken');
            if (tokenFromStorage) {
                this.accessToken = tokenFromStorage;
            }
        }
        return this.accessToken;
    }

    /**
     * Reloads tokens from localStorage
     * Useful when token might have been set in another tab or after login
     */
    reloadTokens(): void {
        this.loadTokensFromStorage();
    }

    /**
     * Health check endpoint
     */
    async healthCheck(): Promise<ApiResponse> {
        try {
            const response = await fetch(getHealthUrl());
            const data = await response.json();

            return {
                success: response.ok,
                message: data.status || 'Health check',
                data: data,
                statusCode: response.status
            };
        } catch (error) {
            throw this.handleNetworkError(error);
        }
    }

    /**
     * Gets user profile
     */
    async getUserProfile(): Promise<ApiResponse<{ user: any }>> {
        return this.get<{ user: any }>('/users/profile');
    }

    async getWithdrawalOtpEligibility(): Promise<
        ApiResponse<{
            canWithdraw: boolean;
            emailVerified: boolean;
            phoneVerified: boolean;
            maskedEmail?: string | null;
            maskedPhone?: string | null;
        }>
    > {
        return this.get('/payment/withdrawal/otp/eligibility', true, true);
    }

    async sendWithdrawalOtp(channel: 'email' | 'phone'): Promise<ApiResponse<{ next_resend_at?: string }>> {
        return this.post('/payment/withdrawal/otp/send', { channel }, true);
    }

    async verifyWithdrawalOtp(
        channel: 'email' | 'phone',
        otp: string
    ): Promise<ApiResponse<{ withdrawChallengeToken: string }>> {
        return this.post('/payment/withdrawal/otp/verify', { channel, otp }, true);
    }

    /**
     * Updates user profile
     */
    async updateUserProfile(profileData: {
        firstName?: string;
        lastName?: string;
        phone?: string;
        address?: {
            street?: string;
            city?: string;
            state?: string;
            zipCode?: string;
            country?: string;
        };
        preferences?: {
            language?: string;
            timezone?: string;
            notifications?: {
                email?: boolean;
                sms?: boolean;
                push?: boolean;
            };
        };
    }): Promise<ApiResponse<{ user: any }>> {
        const response = await this.put<{ user: any }>('/users/profile', { profile: profileData });
        // Clear profile cache after update
        if (response.success) {
            this.clearUserCache();
        }
        return response;
    }

    async changePassword(passwordData: {
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
    }): Promise<ApiResponse<{ message: string; passwordChangedAt: string }>> {
        return this.put<{ message: string; passwordChangedAt: string }>('/users/change-password', passwordData);
    }

    /** New username + temp password to phone/email; 24h rate limit; clears session (must sign in again). */
    async reissueAccountCredentials(): Promise<
        ApiResponse<{ requireLogin?: boolean; lastSelfCredentialReissueAt?: string }>
    > {
        const response = await this.post<{ requireLogin?: boolean; lastSelfCredentialReissueAt?: string }>(
            '/users/account/reissue-credentials',
            {},
            true
        );
        if (response.success) {
            this.clearTokens();
            this.clearUserCache();
        }
        return response;
    }

    /**
     * Send OTP to new email address for email change
     */
    async sendEmailChangeOTP(email: string): Promise<ApiResponse<{ message: string }>> {
        return this.post<{ message: string }>('/users/add-email/send-otp', { email });
    }

    /**
     * Verify OTP and update email address
     */
    async verifyEmailChange(email: string, otp: string): Promise<ApiResponse<{ user: any; message: string }>> {
        const response = await this.post<{ user: any; message: string }>('/users/add-email/verify', { email, otp });
        // Clear profile cache after email change
        if (response.success) {
            this.clearUserCache();
        }
        return response;
    }

    async sendPhoneChangeOTP(phone: string): Promise<ApiResponse<{ message: string }>> {
        return this.post<{ message: string }>('/users/phone/send-otp', { phone });
    }

    async verifyPhoneChange(phone: string, otp: string): Promise<ApiResponse<{ user: any; message: string }>> {
        const response = await this.post<{ user: any; message: string }>('/users/phone/verify', { phone, otp });
        if (response.success) this.clearUserCache();
        return response;
    }

    /**
     * Admin: Approve a withdrawal request
     */
    async approveWithdrawal(transactionId: string, adminNotes?: string): Promise<ApiResponse<any>> {
        return this.put<any>(`/payment/withdrawals/${transactionId}/approve`, { adminNotes });
    }

    /**
     * Admin: Reject a withdrawal request
     */
    async rejectWithdrawal(transactionId: string, adminNotes?: string): Promise<ApiResponse<any>> {
        return this.put<any>(`/payment/withdrawals/${transactionId}/reject`, { adminNotes });
    }

    /**
     * Admin: Get all withdrawal requests (with optional status filter)
     */
    async getWithdrawalsForAdmin(status?: string, limit?: number): Promise<ApiResponse<any>> {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (limit) params.append('limit', limit.toString());
        const query = params.toString();
        const endpoint = query ? `/payment/withdrawals?${query}` : '/payment/withdrawals';
        return this.get<any>(endpoint);
    }

    /**
     * Get withdrawal auto-approve threshold from backend
     * Returns threshold value and currency
     */
    async getWithdrawalThreshold(): Promise<ApiResponse<{
        withdrawalAutoApproveThreshold: number;
        minWithdrawalAmount?: number;
        maxWithdrawalPerDay?: number;
        maturityHours?: number;
        minDepositAmount?: number;
        currency: string;
    }>> {
        return this.get<{
            withdrawalAutoApproveThreshold: number;
            minWithdrawalAmount?: number;
            maxWithdrawalPerDay?: number;
            maturityHours?: number;
            currency: string;
        }>('/config/withdrawal-threshold', true);
    }

    /** Super-admin global messages for the logged-in player */
    async getMyAdminBroadcasts(): Promise<ApiResponse<{
        broadcasts: Array<{
            id: string;
            title: string;
            body: string;
            createdAt: string;
            acknowledged: boolean;
        }>;
    }>> {
        return this.get('/users/me/broadcasts', true, true);
    }

    async acknowledgeAdminBroadcast(broadcastId: string): Promise<ApiResponse<unknown>> {
        return this.post(`/users/me/broadcasts/${broadcastId}/ack`, {}, true);
    }

    /** Modal in-app popups (separate from bell notifications) */
    async getMyUnseenInAppPopups(): Promise<ApiResponse<{
        popups: Array<{
            id: string;
            title: string;
            body: string;
            createdAt: string | null;
            displayMode?: 'once' | 'sticky_daily';
            expiresAt?: string | null;
        }>;
    }>> {
        return this.get('/users/me/in-app-popups/unseen', true, true);
    }

    async markInAppPopupSeen(popupId: string): Promise<ApiResponse<unknown>> {
        return this.post(`/users/me/in-app-popups/${popupId}/seen`, {}, true);
    }

    async markInAppPopupNeverAgain(popupId: string): Promise<ApiResponse<unknown>> {
        return this.post(`/users/me/in-app-popups/${popupId}/never-again`, {}, true);
    }
}

// Export singleton instance
export const apiService = new ApiService();
